import styles from 'css/src/components/dashboard/DashboardSketchInput.module.scss';

import React from 'react';

import { getSketchDestinationUrl, saveDashboardSketchData } from './utils/dashboardSketchStorage';
import { serializeSketchFiles } from './utils/serializeSketchFiles';

import ChatInput from 'js/src/components/app/chat-tab/components/ChatInput';
import { useReferenceOptions } from 'js/src/components/common/mentions/useReferenceOptions';
import { Toast } from 'js/src/components/common/Toast';
import type { UploadedFile } from 'js/src/components/sketch/FileUpload';
import { useLoadSketchData } from 'js/src/components/sketch/hooks/useLoadSketchData';
import { useVideoLimitGate } from 'js/src/hooks/useVideoLimitGate';
import Flags from 'js/src/libs/flags';
import Navigation from 'js/src/libs/navigation';

export interface DashboardSketchInputProps {
    canUseSketch: boolean;
    canUseOutlineAnIdea: boolean;
    canUseAIScript: boolean;
    hideHeader?: boolean;
    hint?: string;
    autoFocus?: boolean;
    onBeforeNavigate?: () => void;
    hideSketchControls?: boolean;
}

const DashboardSketchInput: React.FC<DashboardSketchInputProps> = ({
    canUseSketch,
    canUseOutlineAnIdea,
    canUseAIScript,
    hideHeader = false,
    hint,
    autoFocus = false,
    onBeforeNavigate,
    hideSketchControls = false,
}) => {
    useLoadSketchData();
    const { isOverLimit: isVideoLimitExceeded, showVideoLimitModal } = useVideoLimitGate();

    const handleBeforeSubmit = React.useCallback(
        (messageText: string, attachedFiles: UploadedFile[]) => {
            if (isVideoLimitExceeded) {
                showVideoLimitModal();
                return false;
            }

            if (!canUseSketch) {
                Toast.error('Sketch is not available for your account.');
                return false;
            }

            const { documents, media, voiceovers } = serializeSketchFiles(attachedFiles);

            const saved = saveDashboardSketchData({ messageText, documents, media, voiceovers });
            if (!saved) {
                Toast.error('Unable to transfer your message. Please try with fewer files.');
                return false;
            }

            analytics.track('Dashboard: Sketch Input Submitted', {
                message_length: messageText.length,
                document_count: documents.length,
                media_count: media.length,
                voiceover_count: voiceovers.length,
            });

            return true;
        },
        [canUseSketch, isVideoLimitExceeded, showVideoLimitModal],
    );

    const handleSendMessage = React.useCallback(() => {
        onBeforeNavigate?.();
        Navigation.goToLocation(getSketchDestinationUrl());
    }, [onBeforeNavigate]);

    const hideControls = !canUseSketch || !canUseOutlineAnIdea || !canUseAIScript;

    // Behind the Toolbox flag while mentions are trialled: undefined keeps the
    // input a plain textarea.
    const canUseMentions = Flags.canUseToolbox();
    const referenceOptions = useReferenceOptions(canUseMentions);
    const atMentionReferences = canUseMentions ? referenceOptions : undefined;

    const disabledTooltip = React.useMemo(() => {
        if (!canUseSketch) {
            return 'Chat is disabled for this account. Contact your admin to enable it.';
        }
        if (!canUseOutlineAnIdea || !canUseAIScript) {
            return 'AI features are disabled for this account. Contact your admin to enable them.';
        }
        return '';
    }, [canUseSketch, canUseOutlineAnIdea, canUseAIScript]);

    let header: React.ReactNode;
    if (!hideHeader) {
        header = (
            <>
                <h5 className="font-l5 font-l5--h6">Create a video with chat</h5>
                <p className="font-l5 font-l5--small--medium color-grey-700">
                    Not sure where to start? Share what you have and let chat guide the next steps
                </p>
            </>
        );
    } else if (hint) {
        header = <p className={styles['dashboard-sketch-input__hint']}>{hint}</p>;
    }

    return (
        <ChatInput
            onSendMessage={handleSendMessage}
            atMentionReferences={atMentionReferences}
            placeholder="Provide the link, document, files, or script you'd like to turn into a video."
            header={header}
            hideControls={hideControls}
            disabledTooltip={disabledTooltip}
            className={`${styles['dashboard-sketch-input']} ${hint ? styles['dashboard-sketch-input--with-hint'] : ''}`}
            onBeforeSubmit={handleBeforeSubmit}
            keepInputAfterSubmit={true}
            showSketchControls={!hideSketchControls}
            autoFocus={autoFocus}
            noPadding
            canAttachExistingMedia
        />
    );
};

export default DashboardSketchInput;
